import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { CheckInValidationCount, CheckInValidationType } from "types";
import { CheckInValidation } from "types/src/types/CheckInPreference";
import { CheckInPreferenceFormValues } from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";

export enum MultiCheckInType {
    LIMITED = "multiple",
    UNLIMITED = "unlimited",
}

export const useMultiCheckInFieldValues = (values: CheckInPreferenceFormValues | null, namePrefix = "") => {
    const { setFieldValue: setFormikFieldValue, setFieldTouched } = useFormikContext();
    const validations = values?.validations;

    const setFieldValue = (name: string, value: any) => {
        const fieldName = `${namePrefix}${name}`;
        setFormikFieldValue(fieldName, value);

        /** Why `setTimeout` is added ? https://github.com/jaredpalmer/formik/issues/2083#issuecomment-639962222 */
        setTimeout(() => setFieldTouched(fieldName));
    };

    const setValidations = (validations: CheckInValidation[]) => {
        setFieldValue("validations", validations);
    };

    const countValidation = validations?.find((validation) => validation.object === CheckInValidationType.COUNT) as
        | CheckInValidationCount
        | undefined;

    /** We can directly compute `limit`` using `countValidation.max`, as everytime when user enters a value we update the count validation in validations array. But this may cause a laggy typing experience. So, we have created a separate state for "limit" */
    const [limit, setLimit] = useState<number>(countValidation?.max ?? 0);

    const checkInType: MultiCheckInType = countValidation ? MultiCheckInType.LIMITED : MultiCheckInType.UNLIMITED;

    const handleMultipleCheckInEnabledToggle = (value: boolean) => {
        setFieldValue("multiCheckInEnabled", value);
        const previousValidations = validations ?? [];

        if (value) {
            /** Multi check-in is enabled; we need to add a new `COUNT` validation, with `max=2` as default */
            setValidations(getMutatedValidations(previousValidations, "upsert", 2));
            setLimit(2);

            return;
        }

        /** When multi-check is disabled, we need to update the `COUNT` validation with `max=1` */
        setValidations(getMutatedValidations(previousValidations, "upsert", 1));
        setLimit(1);
    };

    const handleCheckInTypeChange = (value: MultiCheckInType) => {
        const previousValidations = validations ?? [];

        if (value === MultiCheckInType.UNLIMITED) {
            /** For unlimited check-ins, there would be no `COUNT` validation */
            setValidations(getMutatedValidations(previousValidations, "remove"));

            return;
        }

        setValidations(getMutatedValidations(previousValidations, "upsert", 2));
        setLimit(2);
    };

    const handleLimitChange = (value: number) => {
        setLimit(value);

        setValidations(getMutatedValidations(validations!, "upsert", value));
    };

    useEffect(() => {
        if (countValidation) {
            setLimit(countValidation.max ?? 0);
        }
    }, [countValidation]);

    return {
        limit,
        checkInType,
        handleLimitChange,
        handleCheckInTypeChange,
        handleMultipleCheckInEnabledToggle,
    };
};

type MutateAction = "upsert" | "remove";

const getMutatedValidations = (
    validations: CheckInValidation[],
    action: MutateAction,
    max = 1,
): CheckInValidation[] => {
    if (action === "remove") {
        return validations.filter((validation) => validation.object !== CheckInValidationType.COUNT);
    }

    let hasCountValidation = false;

    const mutatedValidations = validations.map((validation) => {
        if (validation.object === CheckInValidationType.COUNT) {
            hasCountValidation = true;
            return { ...validation, max };
        }

        return validation;
    });

    if (!hasCountValidation) {
        mutatedValidations.push({ object: CheckInValidationType.COUNT, max });
    }

    return mutatedValidations;
};

export const hasCountValidation = (validations?: CheckInValidation[]) => {
    if (!validations) return false;

    return validations.some((validation) => validation.object === CheckInValidationType.COUNT);
};
